/**
 * [INPUT]: 依赖 ../scorer 的 ScoreReport 与 tierOf，依赖 ./tier-colors
 * [OUTPUT]: 对外提供 renderCard(report) → PNG dataURL；drawCard(ctx, report) 底层
 * [POS]: apps/web/report 的结果卡渲染层，Canvas 2D 出 PNG；对齐 docs §4.6 结果卡内容
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import QRCode from 'qrcode'
import { type ScoreReport } from '../scorer'

const W = 750
const H = 1040

export function renderCard(report: ScoreReport): string {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')!
  drawCard(ctx, report)
  return canvas.toDataURL('image/png')
}

export function drawCard(ctx: CanvasRenderingContext2D, report: ScoreReport): void {
  // 1. 背景 (深邃暗炭褐色，同 verify.html)
  ctx.fillStyle = '#171513'
  ctx.fillRect(0, 0, W, H)

  // 2. 聚光灯径向渐变
  const cx = W / 2
  const cy = 260
  const spotlight = ctx.createRadialGradient(cx, 80, 50, cx, 80, 350)
  spotlight.addColorStop(0, 'rgba(197, 170, 115, 0.15)') // brass 15% 透明微光
  spotlight.addColorStop(1, 'rgba(23, 21, 19, 0)')
  ctx.fillStyle = spotlight
  ctx.beginPath()
  ctx.arc(cx, 80, 350, 0, Math.PI * 2)
  ctx.fill()

  // 3. 头部标题 (带宽字间距的“PSL 分析报告”)
  ctx.fillStyle = '#9EA4B0'
  ctx.font = '300 24px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('P S L   A N A L Y S I S   R E P O R T', cx, 80)

  // 4. 环形主分表盘
  // 底部阴影轨圈
  ctx.strokeStyle = '#292420' // 悬浮输入背景灰色
  ctx.lineWidth = 10
  ctx.beginPath()
  ctx.arc(cx, cy, 100, 0, Math.PI * 2)
  ctx.stroke()

  // 渐变光弧
  const ringGrad = ctx.createLinearGradient(cx - 100, cy - 100, cx + 100, cy + 100)
  ringGrad.addColorStop(0, '#FFEAA7')
  ringGrad.addColorStop(1, '#E8A34D')
  ctx.strokeStyle = ringGrad
  ctx.lineWidth = 10
  ctx.lineCap = 'round'
  ctx.beginPath()
  // 从正上方 -90deg 开始
  const scorePct = Math.max(0, Math.min(1, report.total / 8.0))
  ctx.arc(cx, cy, 100, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * scorePct)
  ctx.stroke()

  // 表盘内部中心数字
  ctx.fillStyle = '#F3F4F6'
  ctx.font = '600 76px system-ui, -apple-system, sans-serif'
  ctx.fillText(report.total.toFixed(1), cx, cy - 15)

  // 满分标识
  ctx.fillStyle = '#C5AA73' // brass
  ctx.font = '500 22px system-ui, sans-serif'
  ctx.fillText('/ 8.0', cx, cy + 38)

  // 5. 百分比排名描述
  const mean = 4.0
  const stdDev = 1.6
  const z = (report.total - mean) / (stdDev * Math.sqrt(2))
  const t = 1.0 / (1.0 + 0.5 * Math.abs(z))
  const erf = 1.0 - t * Math.exp(-z*z - 1.26551223 + t * (1.00002368 + t * (0.37409196 + t * (0.09678418 + t * (-0.18628806 + t * (0.27886807 + t * (-1.13520398 + t * (1.48851587 + t * (-0.82215223 + t * 0.17087277)))))))));
  let cdf = 0.5 * (1.0 + (z >= 0 ? erf : -erf))
  cdf = Math.max(0.01, Math.min(0.999, cdf))
  const rankPercentile = Math.round(cdf * 100)

  ctx.fillStyle = '#9EA4B0'
  ctx.font = '400 24px system-ui, sans-serif'
  ctx.fillText(`Higher than ${rankPercentile}% of samples`, cx, 415)

  // 6. 六维雷达图 (Radar Chart)
  const rcx = cx
  const rcy = 650
  const maxRadarRadius = 150

  // 绘制三层正六边形网格 (对应 30%, 60%, 100% 比例对齐 SVG)
  const drawHexGrid = (rad: number, dash = false) => {
    ctx.beginPath()
    for (let i = 0; i < 6; i++) {
      const angle = -Math.PI / 2 + (i * Math.PI * 2) / 6
      const x = rcx + rad * Math.cos(angle)
      const y = rcy + rad * Math.sin(angle)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
    ctx.strokeStyle = '#3A3229'
    ctx.lineWidth = 1.5
    if (dash) {
      ctx.setLineDash([4, 4])
    } else {
      ctx.setLineDash([])
    }
    ctx.stroke()
    ctx.setLineDash([])
  }
  drawHexGrid(150)
  drawHexGrid(90, true)
  drawHexGrid(45)

  // 绘制 6 条轴线
  ctx.strokeStyle = '#3A3229'
  ctx.lineWidth = 1
  for (let i = 0; i < 6; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / 6
    ctx.beginPath()
    ctx.moveTo(rcx, rcy)
    ctx.lineTo(rcx + 150 * Math.cos(angle), rcy + 150 * Math.sin(angle))
    ctx.stroke()
  }

  // 计算多边形顶点数据
  const getVal = (k: string) => report.sub_scores[k] ?? 4.0 // 缺省用平均分（新严苛尺度下 6.0=Chadlite 不合理）
  const radarValues = [
    (getVal('harmony_thirds') + getVal('harmony_midface')) / 2, // 面部比例
    getVal('harmony_symmetry'),                                 // 对称性
    getVal('dimorphism_canthal'),                               // 五官和谐
    getVal('presentation_quality'),                             // 肌肤质感
    getVal('angularity_jaw'),                                   // 健康光泽
    (getVal('dimorphism_jawlip') + getVal('angularity_gonial')) / 2, // 年轻态
  ]

  const radarPoints = radarValues.map((val, i) => {
    const r = Math.max(0, Math.min(8.0, val)) / 8.0 * maxRadarRadius
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / 6
    return {
      x: rcx + r * Math.cos(angle),
      y: rcy + r * Math.sin(angle)
    }
  })

  // 绘制雷达填充区域 (珊瑚橙 22% 透明)
  ctx.fillStyle = 'rgba(232, 137, 104, 0.22)'
  ctx.strokeStyle = '#E88968'
  ctx.lineWidth = 3
  ctx.beginPath()
  radarPoints.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y)
    else ctx.lineTo(p.x, p.y)
  })
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // 绘制顶点描边点
  ctx.fillStyle = '#171513'
  ctx.strokeStyle = '#E88968'
  ctx.lineWidth = 2
  radarPoints.forEach((p) => {
    ctx.beginPath()
    ctx.arc(p.x, p.y, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
  })

  // 绘制 6 个顶点的维度标签
  const labels = ['Proportions', 'Symmetry', 'Feature Harmony', 'Skin Quality', 'Jaw Definition', 'Structure']
  ctx.fillStyle = '#9EA4B0'
  ctx.font = '500 18px system-ui, sans-serif'
  ctx.textBaseline = 'middle'
  labels.forEach((label, i) => {
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / 6
    const lx = rcx + 185 * Math.cos(angle)
    const ly = rcy + 185 * Math.sin(angle)
    
    if (i === 0 || i === 3) {
      ctx.textAlign = 'center'
    } else if (i === 1 || i === 2) {
      ctx.textAlign = 'left'
    } else {
      ctx.textAlign = 'right'
    }
    ctx.fillText(label, lx, ly)
  })

  // 7. 图例 (Legends)
  const legY = 880
  ctx.textAlign = 'left'

  // 左图例 (健康指标 -> 橄榄绿)
  ctx.fillStyle = '#8FB56B'
  ctx.beginPath()
  ctx.arc(cx - 85, legY, 5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#9EA4B0'
  ctx.font = '400 16px system-ui, sans-serif'
  ctx.fillText('Health', cx - 73, legY)

  // Right legend (Attractiveness → brass)
  ctx.fillStyle = '#C5AA73'
  ctx.beginPath()
  ctx.arc(cx + 45, legY, 5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#9EA4B0'
  ctx.font = '400 16px system-ui, sans-serif'
  ctx.fillText('Attract.', cx + 57, legY)

  // 8. 页脚与二维码
  ctx.strokeStyle = 'rgba(197, 170, 115, 0.12)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(48, 940)
  ctx.lineTo(W - 48, 940)
  ctx.stroke()

  ctx.fillStyle = '#6E685E'
  ctx.font = '400 16px monospace'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText('pslanalyzer.com', 48, 985)

  // 绘制真实二维码
  try {
    const qrText = 'https://pslanalyzer.com'
    const qr = QRCode.create(qrText, { errorCorrectionLevel: 'M' })
    const size = qr.modules.size
    const qrSize = 48
    const qr_x = W - 48 - qrSize
    const qr_y = 961
    const cellSize = qrSize / size
    const pad = 6

    // 1. 绘制温和的米乳白/暖砂色背景保护区 (Quiet Zone)，避免冷白色的突兀感
    ctx.fillStyle = '#F5EFE6' 
    ctx.fillRect(qr_x - pad, qr_y - pad, qrSize + pad * 2, qrSize + pad * 2)

    // 2. 绘制精致的纤细金色边框，使二维码完美融入轻奢卡片主题
    ctx.strokeStyle = 'rgba(197, 170, 115, 0.35)'
    ctx.lineWidth = 1
    ctx.strokeRect(qr_x - pad, qr_y - pad, qrSize + pad * 2, qrSize + pad * 2)

    // 3. 绘制深色二维码模块，保证极佳对比度与扫描通过率
    ctx.fillStyle = '#1C1917' 
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (qr.modules.get(r, c)) {
          ctx.fillRect(
            Math.round(qr_x + c * cellSize),
            Math.round(qr_y + r * cellSize),
            Math.ceil(cellSize),
            Math.ceil(cellSize)
          )
        }
      }
    }
  } catch (err) {
    console.error('Failed to render QR Code:', err)
  }
}